import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Calendar,
  DateField,
  FormField,
  IconButton,
  Input,
  Modal,
  Popover,
  useToast,
} from '@ntconsult/aluma-react';
import { isAxiosError } from 'axios';
import { format, parseISO } from 'date-fns';
import { Calendar as IconeCalendario } from 'lucide-react';
import { type FocusEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Controller, type ControllerRenderProps, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useCriarCampanha, useEditarCampanha } from '../hooks';
import type { Campanha } from '../types/Campanha';

type Valores = {
  nome: string;
  inicio: Date | null;
  fim: Date | null;
  entregaPrevistaInicio: Date | null;
  entregaPrevistaFim: Date | null;
};

type CampoData = Exclude<keyof Valores, 'nome'>;

const VAZIO: Valores = {
  nome: '',
  inicio: null,
  fim: null,
  entregaPrevistaInicio: null,
  entregaPrevistaFim: null,
};

/** `Date` do DateField → `YYYY-MM-DD` em hora local (nunca `toISOString()`, que é UTC). */
const paraData = (d: Date) => format(d, 'yyyy-MM-dd');

/**
 * O `DateField` emite `null` vazio e incompleto; `preenchido` diz se algum segmento do campo
 * tem dígito, para escolher entre "Campo obrigatório" e "Data inválida." (spec 26 R5).
 */
function useSchema(preenchido: (campo: CampoData) => boolean) {
  const { t } = useTranslation(['common', 'erros', 'campanhas']);
  return useMemo(() => {
    const obrigatorio = t('common:errors.required');
    const invalida = t('campanhas:form.dataInvalida');
    const obrigatoria = (campo: CampoData) =>
      z.date({ error: () => (preenchido(campo) ? invalida : obrigatorio) });
    const opcional = (campo: CampoData) =>
      z
        .date()
        .nullable()
        .refine((d) => d !== null || !preenchido(campo), invalida);
    return (
      z
        .object({
          nome: z.string().trim().min(1, obrigatorio).max(120),
          inicio: obrigatoria('inicio'),
          fim: obrigatoria('fim'),
          entregaPrevistaInicio: opcional('entregaPrevistaInicio'),
          entregaPrevistaFim: opcional('entregaPrevistaFim'),
        })
        .refine((v) => paraData(v.fim) >= paraData(v.inicio), {
          path: ['fim'],
          message: t('erros:PERIODO_INVALIDO'),
        })
        // Spec 21 R1: as duas datas da estimativa ou nenhuma; fim não anterior ao início.
        .refine(
          (v) =>
            (v.entregaPrevistaInicio === null) === (v.entregaPrevistaFim === null) &&
            (!v.entregaPrevistaInicio ||
              !v.entregaPrevistaFim ||
              paraData(v.entregaPrevistaFim) >= paraData(v.entregaPrevistaInicio)),
          { path: ['entregaPrevistaFim'], message: t('erros:ENTREGA_PREVISTA_INVALIDA') },
        )
    );
  }, [t, preenchido]);
}

/**
 * Plano B da spec 34 R18: o calendário ao lado do `DateField`, aberto só pelo botão. O `DatePicker`
 * com `mask` abre o calendário a cada foco, e o de Fim cobria o "Salvar" do modal.
 */
function BotaoCalendario({
  rotulo,
  valor,
  onEscolher,
}: {
  rotulo: string;
  valor: Date | null;
  onEscolher: (d: Date | null) => void;
}) {
  const { t } = useTranslation('campanhas');
  const [aberto, setAberto] = useState(false);
  return (
    <Popover
      open={aberto}
      onOpenChange={setAberto}
      placement="bottom-end"
      padding="none"
      trigger={
        <IconButton
          variant="ghost"
          // Fora do Tab: pelo teclado a data se digita, e o Tab vai de Início a Fim (spec 26 R7).
          tabIndex={-1}
          data-calendario
          label={t('form.calendario', { campo: rotulo })}
          icon={<IconeCalendario aria-hidden />}
        />
      }
    >
      <Calendar
        mode="single"
        selected={valor}
        defaultMonth={valor ?? undefined}
        onSelect={(d) => {
          onEscolher(d);
          setAberto(false);
        }}
      />
    </Popover>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  /** Sem `campanha`: cria. Com `campanha`: edita. */
  campanha?: Campanha;
};

export function CampanhaFormModal({ open, onClose, campanha }: Props) {
  const { t } = useTranslation(['campanhas', 'common', 'erros']);
  const { toast } = useToast();
  const titleId = useId();
  const formId = useId();
  // Grupos dos campos de data: o schema lê os segmentos na hora de validar (spec 26 R5).
  const conteineres = useRef<Partial<Record<CampoData, HTMLDivElement | null>>>({});
  const preenchido = useRef((campo: CampoData) =>
    [...(conteineres.current[campo]?.querySelectorAll('input') ?? [])].some((i) => i.value !== ''),
  ).current;
  const schema = useSchema(preenchido);
  const criar = useCriarCampanha();
  const navigate = useNavigate();
  const editar = useEditarCampanha(campanha?.id ?? '');
  const pending = criar.isPending || editar.isPending;

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<Valores>({
    // `schema` valida o formato final; o resolver recebe os tipos do formulário.
    resolver: zodResolver(schema) as never,
    defaultValues: VAZIO,
    mode: 'onTouched',
  });

  // Reabrir o modal parte sempre do estado atual (campanha ao editar, vazio ao criar).
  useEffect(() => {
    if (!open) return;
    reset(
      campanha
        ? {
            nome: campanha.nome,
            // `parseISO` lê em hora local; `new Date('2026-10-14')` é UTC e mostraria 13/10.
            inicio: parseISO(campanha.inicio),
            fim: parseISO(campanha.fim),
            entregaPrevistaInicio: campanha.entregaPrevistaInicio
              ? parseISO(campanha.entregaPrevistaInicio)
              : null,
            entregaPrevistaFim: campanha.entregaPrevistaFim
              ? parseISO(campanha.entregaPrevistaFim)
              : null,
          }
        : VAZIO,
    );
  }, [open, campanha, reset]);

  /** Um campo de data: segmentos do `DateField` (spec 26 R1) e o botão do calendário (spec 34 R18). */
  function campoData(
    campo: CampoData,
    label: string,
    field: ControllerRenderProps<Valores, CampoData>,
    required?: boolean,
  ) {
    return (
      <FormField label={label} error={errors[campo]?.message} required={required}>
        <div className="flex items-center gap-1">
          <DateField
            className="min-w-0 flex-1"
            value={field.value}
            onChange={field.onChange}
            ariaLabel={label}
            ref={(el) => {
              conteineres.current[campo] = el;
              field.ref(el);
            }}
            // `onTouched` só conta a saída do campo, não a troca entre segmentos nem a ida ao
            // botão do calendário.
            onBlur={(e: FocusEvent<HTMLDivElement>) => {
              const para = e.relatedTarget as HTMLElement | null;
              if (!e.currentTarget.contains(para) && !para?.hasAttribute('data-calendario'))
                field.onBlur();
            }}
          />
          <BotaoCalendario rotulo={label} valor={field.value} onEscolher={field.onChange} />
        </div>
      </FormField>
    );
  }

  async function onSubmit(v: Valores) {
    const input = {
      nome: v.nome.trim(),
      inicio: paraData(v.inicio as Date),
      fim: paraData(v.fim as Date),
      entregaPrevistaInicio: v.entregaPrevistaInicio ? paraData(v.entregaPrevistaInicio) : null,
      entregaPrevistaFim: v.entregaPrevistaFim ? paraData(v.entregaPrevistaFim) : null,
    };
    try {
      if (campanha) {
        await editar.mutateAsync(input);
        onClose();
      } else {
        const criada = await criar.mutateAsync(input);
        onClose();
        navigate(`/campanhas/${criada.id}`); // spec 28 R11: o detalhe mostra a página da fase
      }
    } catch (error) {
      const chave = mensagemDeErro(error);
      // 422 volta como erro de campo em "Fim"; o resto, toast.
      const codigo = isAxiosError(error) ? error.response?.data?.codigo : null;
      if (codigo === 'PERIODO_INVALIDO') {
        setError('fim', { message: t(`erros:${chave}`) });
      } else if (codigo === 'ENTREGA_PREVISTA_INVALIDA') {
        setError('entregaPrevistaFim', { message: t(`erros:${chave}`) });
      } else {
        toast.danger({ title: t(`erros:${chave}`) });
      }
    }
  }

  return (
    <Modal open={open} onClose={onClose} ariaLabelledBy={titleId} size="md">
      <Modal.Header
        titleId={titleId}
        title={campanha ? t('campanhas:acoes.editar') : t('campanhas:nova')}
        onClose={onClose}
      />
      <Modal.Body>
        <form
          id={formId}
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
          noValidate
        >
          <FormField label={t('campanhas:form.nome')} error={errors.nome?.message} required>
            <Input autoFocus maxLength={120} {...register('nome')} />
          </FormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <Controller
              control={control}
              name="inicio"
              render={({ field }) => campoData('inicio', t('campanhas:form.inicio'), field, true)}
            />
            <Controller
              control={control}
              name="fim"
              render={({ field }) => campoData('fim', t('campanhas:form.fim'), field, true)}
            />
          </div>

          <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0">
            <legend className="text-sm font-medium text-foreground">
              {t('campanhas:form.entregaPrevista')}
            </legend>
            <p className="text-sm text-foreground-muted">
              {t('campanhas:form.entregaPrevistaHint')}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Controller
                control={control}
                name="entregaPrevistaInicio"
                render={({ field }) =>
                  campoData('entregaPrevistaInicio', t('campanhas:form.entregaInicio'), field)
                }
              />
              <Controller
                control={control}
                name="entregaPrevistaFim"
                render={({ field }) =>
                  campoData('entregaPrevistaFim', t('campanhas:form.entregaFim'), field)
                }
              />
            </div>
          </fieldset>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          {t('common:cancel')}
        </Button>
        <Button type="submit" form={formId} variant="primary" loading={pending}>
          {t('common:save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
